import {
  type ClientToolResponse,
  MAX_CLIENT_TOOL_RESULT_BYTES,
} from "@magnus/contract/client-tools";

/**
 * POST a client-side tool result back to the Magnus backend so the paused
 * workflow webhook can resume. Generalizes the app-local poster in
 * `apps/web/hooks/use-client-tool-executor.tsx` by taking the base URL +
 * an auth-applied fetch instead of hardcoding `/api/chat/tool-results`.
 *
 * Returns `true` when the backend accepted the result.
 */
export async function postToolResult(opts: {
  baseUrl: string;
  fetch: typeof globalThis.fetch;
  response: ClientToolResponse;
}): Promise<boolean> {
  const res = await opts.fetch(`${opts.baseUrl}/api/chat/tool-results`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: serializeToolResult(opts.response),
  });
  return res.ok;
}

/**
 * The POST body for a tool result, or an error result in its place when it
 * would exceed {@link MAX_CLIENT_TOOL_RESULT_BYTES}: an oversized body is
 * refused by the host before the route sees it, and the run would then wait
 * out its deadline rather than learn that it asked for too much.
 */
export function serializeToolResult(response: ClientToolResponse): string {
  const body = JSON.stringify(response);
  // A UTF-16 code unit encodes to at most 3 UTF-8 bytes, so a short body
  // skips the encode.
  if (body.length * 3 <= MAX_CLIENT_TOOL_RESULT_BYTES) {
    return body;
  }
  const bytes = new TextEncoder().encode(body).byteLength;
  if (bytes <= MAX_CLIENT_TOOL_RESULT_BYTES) {
    return body;
  }
  const replacement: ClientToolResponse = {
    toolCallId: response.toolCallId,
    status: "error",
    error:
      `The tool's result (~${toMb(bytes)} MB) is over the ` +
      `${toMb(MAX_CLIENT_TOOL_RESULT_BYTES)} MB a client tool can return. ` +
      "Ask for less per call (a smaller range or fewer rows) and split the " +
      "work across several calls.",
    executionTimeMs: response.executionTimeMs,
  };
  return JSON.stringify(replacement);
}

function toMb(bytes: number): string {
  return (bytes / 1_000_000).toFixed(1);
}
